<template>
    <div>
        <v-data-table
            :headers="filteredHeaders"
            :items="workOrdersFiltered"
            class="elevation-0"
            :mobile-breakpoint="0"
            :loading="loading"
            fixed-header
            :height="$vuetify.breakpoint.mdAndDown ? '62vh' : '62vh'"
            :expanded.sync="expanded"
            show-expand
            :items-per-page="30"
            :footer-props="{
                itemsPerPageOptions: [15, 30, 100],
                itemsPerPageText: 'Work Orders Per Page',
            }"
        >
            <template v-slot:top>
                <v-row
                    no-gutter
                    class="mt-0 mx-0 pt-2 pb-0 pb-4"
                    :style="{ 'background-color': '#eeeeee' }"
                >
                    <v-col
                        cols="12"
                        class="d-flex mb-n3 pt-0 align-center justify-center"
                    >
                        <v-btn
                            color="primary"
                            class="ml-2"
                            @click="openExportReportDialog"
                            :disabled="loading"
                        >
                            <v-icon left>mdi-file-excel</v-icon>
                            Export
                        </v-btn>
                        <ValidationWorkOrders
                            :project-id="projectId || undefined"
                            :user="user"
                            :project-manager="
                                project?.projectManager || undefined
                            "
                        />
                        <div
                            class="d-flex align-center flex-wrap progress-toolbar-filters ml-4 ml-md-5"
                        >
                            <v-combobox
                                v-model="selectedProcess"
                                hide-details
                                dense
                                label="Process"
                                :items="availableProcesses"
                                class="progress-filter-input mb-0"
                                style="z-index: 100; max-width: 200px;"
                            />
                            <v-select
                                v-model="selectedClassification"
                                :items="classificationOptions"
                                label="Classification"
                                placeholder="All"
                                hide-details
                                dense
                                clearable
                                class="progress-filter-input mb-0"
                                style="max-width: 200px;"
                            />
                        </div>

                        <v-spacer />

                        <div
                            class="d-flex my-0"
                            v-for="(insight, index) of insights"
                            :key="index"
                        >
                            <v-card
                                class="pt-3 px-1 ma-1"
                                width="190px"
                                height="65px"
                            >
                                <v-card-text
                                    class="d-flex my-0 py-0 mx-0 px-0"
                                    width="100%"
                                >
                                    <v-chip
                                        class="mb-1 mt-0 mx-2 pt-0 white--text"
                                        :color="
                                            getProgressColor(
                                                insight.key,
                                                insightAggregates[insight.key]
                                            )
                                        "
                                        label
                                        :style="{ height: '40px' }"
                                    >
                                        <v-icon center>
                                            {{ insight.icon }}
                                        </v-icon>
                                    </v-chip>
                                    <div class="mb-1 mt-0 pt-0">
                                        <div
                                            class="d-flex mr-0 pr-0 text-capitalize"
                                        >
                                            <span
                                                class="text-h7"
                                                :style="{ 'font-size': '12px' }"
                                                >{{ insight.title }}</span
                                            >
                                        </div>
                                        <p class="text-h7 black--text">
                                            {{ insightAggregates[insight.key] }}
                                            {{
                                                insight.key != 'count'
                                                    ? '%'
                                                    : ''
                                            }}
                                        </p>
                                    </div>
                                </v-card-text>
                            </v-card>
                        </div>
                    </v-col>
                </v-row>
            </template>
            <!--HEADERS-->
            <template v-slot:[`header.code`]="{ header }">
                <v-text-field
                    class="py-2 mt-2"
                    :label="header.text"
                    v-model="search"
                    dense
                />
            </template>
            <template v-slot:[`header.project`]="{ header }">
                <v-text-field
                    class="py-2 mt-2"
                    :label="header.text"
                    v-model="projectToFilter"
                    dense
                />
            </template>
            <template v-slot:[`header.deliveryDate`]="{ header }">
                <v-text-field
                    class="py-2 mt-2"
                    :label="header.text"
                    v-model="deliveryDateFilter"
                    dense
                />
            </template>
            <template v-slot:[`header.process`]="{ header }">
                <v-row class="d-flex align center justify-center">
                    <p class="ma-0 pa-0 d-flex justify-center align-center">
                        {{ header.text }}
                    </p>
                </v-row>
            </template>
            <!--ITEMS-->
            <template v-slot:[`item.projectManager`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">{{ item.projectManager.name }}</p>
                </div>
            </template>
            <template v-slot:[`item.project`]="{ item }">
                <div class="d-flex justify-center" :class="['fixed-column']">
                    <p class="my-0">
                        {{ item.project?.reference }} - {{ item.project?.name }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.progress`]="{ item }">
                <div class="d-flex justify-center">
                    <v-progress-linear
                        v-model="item.progress"
                        height="25"
                        :color="getProgressColor('progress', item.progress)"
                        rounded
                        class="no-interaction"
                    >
                        <strong>{{ item.progress.toFixed(1) }}%</strong>
                    </v-progress-linear>
                </div>
            </template>
            <template v-slot:[`item.releasedPercentage`]="{ item }">
                <div class="d-flex justify-center">
                    <v-progress-linear
                        v-model="item.releasedPercentage"
                        height="25"
                        :color="
                            getProgressColor('quality', item.releasedPercentage)
                        "
                        rounded
                        class="no-interaction"
                    >
                        <strong>{{
                            item.showReleased
                                ? `${item.releasedPercentage.toFixed(1)}%`
                                : 'NA'
                        }}</strong>
                    </v-progress-linear>
                </div>
            </template>
            <template v-slot:[`item.deliveryDate`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">{{ item.deliveryDate || '—' }}</p>
                </div>
            </template>
            <template v-slot:[`item.process`]="{ item, header }">
                <div class="d-flex justify-center align-center">
                    <p
                        :style="{ margin: 0 }"
                        v-if="item.progressData && item.progressData[header.id]"
                    >
                        {{ item.progressData[header.id].produced }}/{{
                            item.progressData[header.id].total
                        }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
                <div class="d-flex justify-center">
                    <v-menu rounded offset-y :z-index="20">
                        <template v-slot:activator="{ attrs, on }">
                            <v-btn
                                icon
                                v-bind="attrs"
                                v-on="on"
                                small
                                depressed
                            >
                                <v-icon>mdi-dots-vertical</v-icon>
                            </v-btn>
                        </template>
                        <v-list dense style="z-index: 20">
                            <v-list-item
                                @click="openFinishDialog(item)"
                                v-if="
                                    item.status == 'approved' &&
                                        permissionToFinishWO
                                "
                                :disabled="disableFinishProcess(item)"
                            >
                                <v-list-item-icon>
                                    <v-icon>mdi-check-circle</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>
                                    Finish Work Order
                                </v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </div>
            </template>
            <template
                v-slot:expanded-item="{
                    item: item,
                }"
            >
                <td
                    :colspan="
                        (item.itemSubheaders && item.itemSubheaders.length) ||
                            subheaders.length
                    "
                    class="pa-4 ma-0 outlined"
                    style="z-index: 15; position: relative;"
                >
                    <v-row no-gutters>
                        <v-col cols="12" class="subtable">
                            <v-data-table
                                :headers="item.itemSubheaders || subheaders"
                                :items="item.items"
                                class="elevation-0"
                                :mobile-breakpoint="0"
                                :loading="loading"
                                :items-per-page="15"
                                :hide-default-footer="
                                    !item.items || item.items.length <= 15
                                "
                            >
                                <!--ITEMS-->
                                <template v-slot:[`item.qty`]="{ item }">
                                    <div class="d-flex justify-center">
                                        <p class="my-0">
                                            {{ item.quantity }}
                                        </p>
                                    </div>
                                </template>
                                <template v-slot:[`item.progress`]="{ item }">
                                    <div class="d-flex justify-center">
                                        <v-progress-linear
                                            v-model="item.progress"
                                            height="25"
                                            :color="
                                                getProgressColor(
                                                    'progress',
                                                    item.progress
                                                )
                                            "
                                            rounded
                                            class="no-interaction"
                                        >
                                            <strong
                                                >{{
                                                    item.progress.toFixed(1)
                                                }}%</strong
                                            >
                                        </v-progress-linear>
                                    </div>
                                </template>
                                <template
                                    v-slot:[`item.releasedPercentage`]="{
                                        item,
                                    }"
                                >
                                    <div class="d-flex justify-center">
                                        <v-progress-linear
                                            v-model="item.releasedPercentage"
                                            height="25"
                                            :color="
                                                getProgressColor(
                                                    'quality',
                                                    item.releasedPercentage || 0
                                                )
                                            "
                                            rounded
                                            class="no-interaction"
                                        >
                                            <strong>{{
                                                item.showReleased
                                                    ? `${item.releasedPercentage.toFixed(
                                                          1
                                                      )}%`
                                                    : 'NA'
                                            }}</strong>
                                        </v-progress-linear>
                                    </div>
                                </template>
                                <template
                                    v-slot:[`item.process`]="{ item, header }"
                                >
                                    <div
                                        class="d-flex justify-center align-center"
                                        style="z-index: -2;"
                                    >
                                        <p :style="{ margin: 0 }">
                                            {{
                                                item.progressData[header.id] ||
                                                    0
                                            }}
                                        </p>
                                    </div>
                                </template>
                            </v-data-table>
                        </v-col>
                    </v-row>
                </td>
            </template>
            <!-- FOOTER -->
            <template v-slot:[`body.append`]>
                <tr
                    :style="{
                        'background-color': '#eeeeee',
                        position: 'sticky',
                        bottom: '0',
                        'z-index': '10',
                    }"
                >
                    <td :colspan="projectId ? 7 : 9"></td>
                    <td
                        v-for="process in processColumns"
                        :key="process.id"
                        class="text-center"
                    >
                        {{
                            `${processFooterMap[process.id].produced}/${
                                processFooterMap[process.id].total
                            }`
                        }}
                        <br />
                        {{ `${processFooterMap[process.id].progress} %` }}
                        <br />
                        {{
                            `${processFooterMap[process.id].workOrdersCount} WO`
                        }}
                    </td>
                </tr>
            </template>
        </v-data-table>
        <!--Finish Work Order Dialog-->
        <v-dialog
            v-model="openWarning"
            persistent
            max-width="400px"
            :retain-focus="false"
        >
            <v-card :loading="loading" v-if="openWarning">
                <v-card-title class="text-h5">
                    {{ title }} Work Order
                    {{ selectedWorkOrder ? selectedWorkOrder.code : '' }}
                </v-card-title>
                <v-card-text>
                    {{ message }}
                </v-card-text>
                <v-card-actions>
                    <v-btn text color="secondary" @click="closeWarning" rounded>
                        NO
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        @click="finishWO"
                        text
                        color="error"
                        rounded
                        :loading="loading"
                    >
                        YES
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog
            v-model="exportReportDialog"
            max-width="400"
            persistent
            :loading="loading"
        >
            <v-card>
                <v-card-title class="text-h6">
                    <v-btn text @click="exportReportDialog = false" icon>
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                    Generate Report
                </v-card-title>
                <v-card-text>
                    What report do you want to download?
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn
                        text
                        color="primary"
                        @click="downloadReport('basic')"
                    >
                        Basic Report
                    </v-btn>
                    <v-btn
                        text
                        color="primary"
                        @click="downloadReport('extended')"
                    >
                        Extended Report
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { firestore } from '@/services/firebase'
import API from '@/services/api'
import { mapMutations } from 'vuex'
import moment from 'moment'
import WOMethods from './WOMethods.js'

const PROGRESS_PART_NUMBER_KEYS = [
    'partNumber',
    'description',
    'finishAndUCCode',
    'quantity',
    'processes',
    'packedQty',
    'production',
    'quality',
    'deliveries',
]
const FIRESTORE_IN_LIMIT = 10

export default {
    name: 'Deliveries',
    props: {
        projectId: {
            type: String,
            default: () => undefined,
        },
        project: {
            type: Object,
            deafult: () => ({}),
        },
        eventWorkOrders: {
            type: Array,
            deafult: () => undefined,
        },
    },
    components: {
        ValidationWorkOrders: () =>
            import('@/components/WorkOrders/ValidationWorkOrders.vue'),
    },
    data: () => ({
        projectToFilter: undefined,
        deliveryDateFilter: '',
        loading: false,
        workOrders: [],
        search: null,
        exportReportDialog: false,
        headers: [
            {
                text: 'ORDER',
                value: 'code',
                align: 'center',
                sortable: false,
                width: 120,
            },
            {
                text: 'PROJECT MANAGER',
                value: 'projectManager',
                align: 'center',
                sortable: false,
                width: 220,
            },
            {
                text: 'PROJECT',
                value: 'project',
                align: 'center',
                sortable: false,
                width: 240,
            },
            {
                text: 'QTY',
                value: 'woQty',
                align: 'center',
                sortable: false,
                width: 120,
            },
            {
                text: 'PROGRESS',
                value: 'progress',
                align: 'center',
                sortable: true,
                width: 120,
            },
            {
                text: 'QC RELEASED',
                value: 'releasedPercentage',
                align: 'center',
                sortable: true,
                width: 120,
                divider: true,
            },
            {
                text: 'DELIVERY DATE',
                value: 'deliveryDate',
                align: 'center',
                sortable: true,
                width: 130,
                divider: true,
            },
            {
                text: 'ACTIONS',
                value: 'actions',
                align: 'center',
                sortable: false,
                width: 100,
            },
        ],
        subheaders: [
            {
                text: 'PART NUMBER',
                value: 'partNumber',
                align: 'center',
                sortable: false,
                width: 200,
            },
            {
                text: 'DESCRIPTION',
                value: 'description',
                align: 'center',
                sortable: false,
                width: 220,
            },
            {
                text: 'QTY',
                value: 'quantity',
                align: 'center',
                sortable: false,
                width: 100,
            },
            {
                text: 'PROGRESS',
                value: 'progress',
                align: 'center',
                sortable: true,
                divider: true,
                width: 120,
            },
            {
                text: 'QC RELEASED',
                value: 'releasedPercentage',
                align: 'center',
                sortable: true,
                width: 120,
                divider: true,
            },
        ],
        processHeaders: [],
        company: JSON.parse(localStorage.getItem('company')),
        listener: undefined,
        companyId: JSON.parse(localStorage.getItem('company')),
        settingProcesses: undefined,
        finishAndUcCodeNames: {},
        projects: {},
        users: {},
        readyForPackingProcess: {
            id: 'readyForPacking',
            name: 'Ready for packing',
        },
        packingProcess: {
            id: 'packing',
            name: 'Packing',
        },
        qualityProcess: {
            id: 'quality',
            name: 'quality',
        },
        structuredWorkOrders: [],
        expanded: [],
        insights: [
            {
                key: 'count',
                color: 'primary',
                iconColor: '#1A5276',
                icon: 'mdi-factory',
                title: 'Work Orders',
            },
            {
                key: 'production',
                color: '#19405c',
                iconColor: '#1A5276',
                icon: 'mdi-tools',
                title: 'Production Progress',
            },
            {
                key: 'quality',
                color: '#0b1c28',
                iconColor: '#1A5276',
                icon: 'mdi-check',
                title: 'Quality Progress',
            },
        ],
        availableProcesses: [],
        processesInTable: [],
        selectedProcess: null,
        processData: {},
        height: 0,
        localProject: {},
        totalReleasable: 0,
        totalReleased: 0,
        deletedWorkOrdersIds: [],
        user: {},
        permissionToFinishWO: false,
        openWarning: false,
        title: '',
        message: '',
        selectedWorkOrder: null,
        woMethods: new WOMethods(),
        additionalReportWorkOrders: [],
        initialLoad: true,
        selectedClassification: null,
        classificationOptions: ['Nacional', 'Exportación', 'ES'],
        packingListsLite: [],
        deliveryDatesPerWO: {},
        filterByProject: false,
        listeners: [],
        lastLoadMetrics: null,
    }),
    computed: {
        workOrdersFiltered() {
            let rows = this.structuredWorkOrders
            if (this.search) {
                rows = rows.filter(this.filterOrder)
            }
            if (this.projectToFilter) {
                rows = rows.filter(this.filterProject)
            }
            if (this.selectedClassification) {
                rows = rows.filter(this.filterByClassification)
            }
            if (this.selectedProcess) {
                rows = rows.filter(this.filterByProcess)
            }
            if ((this.deliveryDateFilter || '').trim()) {
                rows = rows.filter(this.filterByDeliveryDate)
            }
            return rows
        },
        insightAggregates() {
            const rows = this.workOrdersFiltered
            let productionWeighted = 0
            let productionQty = 0
            let qualityWeighted = 0
            let qualityQty = 0
            rows.forEach(workOrder => {
                productionWeighted +=
                    Number(workOrder.progress || 0) *
                    Number(workOrder.woQty || 0)
                productionQty += Number(workOrder.woQty || 0)
                qualityWeighted +=
                    Number(workOrder.releasedPercentage || 0) *
                    Number(workOrder.totalQualityItems || 0)
                qualityQty += Number(workOrder.totalQualityItems || 0)
            })
            return {
                count: String(rows.length),
                production: (productionQty > 0
                    ? productionWeighted / productionQty
                    : 100
                ).toFixed(1),
                quality: (qualityQty > 0
                    ? qualityWeighted / qualityQty
                    : 100
                ).toFixed(1),
            }
        },
        processColumns() {
            return (this.filteredHeaders || [])
                .filter(header => header.value === 'process')
                .map(process => ({
                    text:
                        process.text.charAt(0).toUpperCase() +
                        process.text.slice(1).toLowerCase(),
                    id: process.id,
                }))
        },
        processFooterMap() {
            const map = {}
            const rows = this.workOrdersFiltered
            this.processColumns.forEach(process => {
                let workOrdersCount = 0
                let produced = 0
                let total = 0
                rows.forEach(workOrder => {
                    const details =
                        workOrder.progressData &&
                        workOrder.progressData[process.id]
                    if (details) {
                        workOrdersCount++
                        produced += +details.produced || 0
                        total += +details.total || 0
                    }
                })
                const progress = total > 0 ? (produced / total) * 100 : 100
                map[process.id] = {
                    workOrdersCount: workOrdersCount.toFixed(0),
                    produced: produced.toFixed(0),
                    total: total.toFixed(0),
                    progress: progress.toFixed(0),
                }
            })
            return map
        },
        filteredHeaders() {
            let headers = []
            let addPacking = false
            let addQuality = false
            headers.push(...this.headers)
            this.workOrdersFiltered.forEach(w => {
                if (w.items) {
                    w.items.forEach(i => {
                        if (i.progressData) {
                            Object.keys(i.progressData).forEach(processId => {
                                if (processId == this.packingProcess.id) {
                                    addPacking = true
                                } else if (
                                    processId == this.qualityProcess.id
                                ) {
                                    addQuality = true
                                } else if (
                                    !headers.find(h => h.id == processId)
                                ) {
                                    const processHeader = this.processHeaders.find(
                                        header => header.id == processId
                                    )
                                    if (processHeader) {
                                        headers.push(processHeader)
                                    }
                                }
                            })
                        }
                    })
                }
            })
            if (addPacking) {
                headers.push({
                    text: this.packingProcess.name.toUpperCase(),
                    value: 'process',
                    id: this.packingProcess.id,
                    sortable: false,
                    align: 'center',
                    divider: true,
                    width: 110,
                })
            }
            if (addQuality) {
                headers.push({
                    text: this.qualityProcess.name.toUpperCase(),
                    value: 'process',
                    id: this.qualityProcess.id,
                    sortable: false,
                    align: 'center',
                    divider: true,
                    width: 110,
                })
            }
            return headers
        },
    },
    watch: {
        processColumns: {
            immediate: true,
            handler(processes) {
                this.processesInTable = processes
                if (
                    this.availableProcesses.length == 0 &&
                    processes.length > 0
                ) {
                    this.availableProcesses = processes
                }
            },
        },
    },
    async mounted() {
        try {
            this.loading = true
            const projectFilterRoutes = [
                'project-wo-progress',
                'archive-project-wo-progress',
                'project-wo-progress-generic',
            ]
            const filterByCodes =
                this.eventWorkOrders && this.eventWorkOrders.length > 0
            this.filterByProject =
                !filterByCodes &&
                (projectFilterRoutes.includes(this.$router.currentRoute.name) ||
                    !!this.projectId)
            // Get project
            this.localProject = this.project
            if (this.projectId && !this.localProject) {
                this.localProject = await API.getProject(this.projectId)
            }
            this.onResize()
            //remove project column in project details view
            if (this.projectId) {
                this.headers.splice(1, 2)
            }
            //add processes columns
            const {
                data: { settings },
            } = await API.getSettings()
            this.settingProcesses = settings.find(s => s.name == 'Processes')
            const settingFinish = settings.find(
                s => s.name == 'FinishAndUCCode'
            )
            const finishAndUcCodeNames = {}
            ;(settingFinish?.finishes || []).forEach(finish => {
                if (finish?.id) {
                    finishAndUcCodeNames[finish.id] = finish.name || ''
                }
            })
            this.finishAndUcCodeNames = finishAndUcCodeNames
            this.settingProcesses.processes.forEach(process => {
                this.processHeaders.push({
                    text: process.name.toUpperCase(),
                    value: 'process',
                    id: process.id,
                    sortable: false,
                    align: 'center',
                    divider: true,
                    width: 120,
                })
            })

            if (this.filterByProject) {
                this.projects = {}
                if (this.localProject && this.projectId) {
                    this.projects[this.projectId] = this.localProject
                }
                this.users = {}
            } else {
                this.projects = await API.getWoProjects()
                const {
                    data: { users },
                } = await API.getWoUsers()
                this.users = users
            }

            // Get user info and permissions
            const {
                data: { user },
            } = await API.getMyInfo()
            this.user = user
            this.permissionToFinishWO = user.permissions.includes('finishWO')

            await this.loadPackingListsDeliveryDates()
            await this.getWOFromSDK()
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    beforeDestroy() {
        this.loadToken = (this.loadToken || 0) + 1
        this.clearListeners()
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        packingListLiteParseDate(value) {
            if (!value) return null
            if (moment.isMoment(value)) return value
            if (value instanceof Date) return moment(value)
            if (typeof value === 'string') {
                const m = moment(value)
                return m.isValid() ? m : null
            }
            if (typeof value === 'object') {
                if (typeof value.toDate === 'function') {
                    const m = moment(value.toDate())
                    return m.isValid() ? m : null
                }
                const seconds = value.seconds || value._seconds
                if (seconds) {
                    return moment.unix(seconds)
                }
            }
            try {
                const parsed = JSON.parse(value)
                const seconds = parsed.seconds || parsed._seconds
                if (seconds) {
                    return moment.unix(seconds)
                }
            } catch (e) {
                void e
            }
            return null
        },
        packingListLiteNormalize(packingLists) {
            const list = packingLists.filter(pl => pl.status !== 'deleted')
            list.forEach(pl => {
                if (pl.selectedWorkOrders) {
                    pl.selectedWorkOrders = pl.selectedWorkOrders.map(wo =>
                        typeof wo === 'object' && wo != null && wo.code != null
                            ? wo.code
                            : wo
                    )
                }
            })
            return list
        },
        packingListLiteDeliveryContext(woCode, packingLists) {
            const WOpackingLists = packingLists.filter(pl =>
                pl.selectedWorkOrders?.includes(woCode)
            )
            let relevantPl = null
            let scheduledForMoment = null
            if (WOpackingLists.length > 0) {
                const excludedStatuses = ['completed', 'shipped']
                const hasValidScheduledFor = pl => {
                    const sched = this.packingListLiteParseDate(pl.scheduledFor)
                    return Boolean(sched?.isValid())
                }
                const datedLists = WOpackingLists.filter(hasValidScheduledFor)
                const candidates =
                    datedLists.length > 0 ? datedLists : WOpackingLists
                const notShippedPl = candidates.filter(
                    pl => !excludedStatuses.includes(pl.status)
                )
                const sortByScheduledFor = (a, b) => {
                    const schedA = this.packingListLiteParseDate(a.scheduledFor)
                    const schedB = this.packingListLiteParseDate(b.scheduledFor)
                    return (
                        (schedA ? schedA.valueOf() : 0) -
                        (schedB ? schedB.valueOf() : 0)
                    )
                }
                if (notShippedPl.length > 0) {
                    const sorted = [...notShippedPl].sort(sortByScheduledFor)
                    relevantPl = sorted[0]
                } else {
                    const sorted = [...candidates].sort(sortByScheduledFor)
                    relevantPl = sorted[sorted.length - 1]
                }
                if (relevantPl?.scheduledFor) {
                    const sched = this.packingListLiteParseDate(
                        relevantPl.scheduledFor
                    )
                    if (sched?.isValid()) {
                        scheduledForMoment = sched
                    }
                }
            }
            return {
                scheduledForMoment,
                relevantPl,
                woPackingLists: WOpackingLists,
            }
        },
        buildDeliveryDatesPerWO(packingLists) {
            const codes = new Set()
            packingLists.forEach(pl => {
                ;(pl.selectedWorkOrders || []).forEach(c => codes.add(c))
            })
            const map = {}
            codes.forEach(code => {
                const m = this.packingListLiteDeliveryContext(
                    code,
                    packingLists
                ).scheduledForMoment
                map[code] = m?.isValid() ? m.format('YYYY-MM-DD') : null
            })
            return map
        },
        async loadPackingListsDeliveryDates() {
            try {
                const raw = await API.getWOPackingListsLite()
                const lists = this.packingListLiteNormalize(raw)
                this.packingListsLite = lists
                this.deliveryDatesPerWO = this.buildDeliveryDatesPerWO(lists)
            } catch (error) {
                this.packingListsLite = []
                this.deliveryDatesPerWO = {}
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        onResize() {
            this.height = window.innerHeight + 1000
        },
        filterOrder(order) {
            return order.code.toLowerCase().includes(this.search.toLowerCase())
        },
        filterProject(order) {
            return (
                order?.project?.name
                    .toLowerCase()
                    .includes(this.projectToFilter.toLowerCase()) ||
                order?.project?.reference
                    .toString()
                    .toLowerCase()
                    .includes(this.projectToFilter.toLowerCase())
            )
        },
        filterByClassification(order) {
            return (
                order.classification?.toLowerCase() ===
                this.selectedClassification?.toLowerCase()
            )
        },
        filterByDeliveryDate(order) {
            const raw = order.deliveryDate
            const q = (this.deliveryDateFilter || '').trim().toLowerCase()
            if (!q) return true
            const hay = raw ? String(raw).toLowerCase() : ''
            return hay.includes(q)
        },
        structureData(workOrders) {
            this.totalReleased = 0
            this.totalReleasable = 0
            const structuredWorkOrders = (workOrders || []).map(workOrder =>
                this.structureOne(workOrder)
            )
            structuredWorkOrders.sort(
                (a, b) => (b.progress || 0) - (a.progress || 0)
            )
            return structuredWorkOrders
        },
        structureOne(workOrder) {
            if (workOrder.items) {
                workOrder.items.forEach(item => {
                    if (!Array.isArray(item.processes)) {
                        item.processes = []
                    }
                    item.progressData = {}
                    if (item.processes) {
                        item.processes.forEach(processId => {
                            let process = processId
                            // asign another id if are required
                            if (processId == this.readyForPackingProcess.id) {
                                process = this.packingProcess.id
                            } else if (processId == this.qualityProcess.id) {
                                process = this.qualityProcess.id
                            }
                            item.progressData[process] = 0
                        })
                    }
                    if (item.production) {
                        item.production.forEach(entry => {
                            if (
                                entry.process.id ==
                                this.readyForPackingProcess.id
                            ) {
                                entry.process.id == this.packingProcess.id
                            }

                            const entryProcess =
                                entry.process.id ==
                                this.readyForPackingProcess.id
                                    ? this.packingProcess.id
                                    : entry.process.id

                            if (entryProcess != this.packingProcess.id) {
                                item.progressData[entryProcess] =
                                    item.progressData[entryProcess] || 0
                                item.progressData[entryProcess] += Number(
                                    entry.qty
                                )
                            }
                        })
                    }
                    if (item.packedQty) {
                        item.progressData[this.packingProcess.id] = Number(
                            item.packedQty
                        )
                    }
                    Object.keys(item.progressData).forEach(processId => {
                        if (
                            processId != this.packingProcess.id &&
                            processId != this.qualityProcess.id
                        ) {
                            item.progressData[processId] = Math.min(
                                Number(item.progressData[processId] || 0),
                                Number(item.quantity)
                            )
                        }
                    })
                    item.progress = 0
                    let progressDataCount = 0
                    Object.keys(item.progressData).forEach(processId => {
                        if (
                            processId != this.packingProcess.id &&
                            processId != this.qualityProcess.id
                        ) {
                            item.progress += Number(
                                item.progressData[processId] || 0
                            )
                            progressDataCount++
                        }
                    })
                    item.progress =
                        progressDataCount > 0
                            ? (item.progress /
                                  (item.quantity * progressDataCount)) *
                              100
                            : 0
                    item.progress = Math.min(item.progress, 100)
                    item.released = 0
                    item.releasableQty = 0
                    if (item.processes.includes(this.qualityProcess.id)) {
                        if (item.quality) {
                            item.quality.forEach(entry => {
                                item.released += Number(entry.releaseQty || 0)
                                this.totalReleased += Number(
                                    entry.releaseQty || 0
                                )
                            })
                        }
                        item.releasableQty += Number(item.quantity)
                        this.totalReleasable += +item.quantity || 0

                        // sum to process the released quantity
                        item.progressData[this.qualityProcess.id] =
                            item.released
                        item.showReleased = true
                    } else if (workOrder.assemblyWorkOrder == undefined) {
                        if (item.quality) {
                            item.quality.forEach(entry => {
                                item.released += Number(entry.releaseQty || 0)
                            })
                        }
                        item.showReleased = true
                    }
                    item.releasedPercentage =
                        (item.released / item.quantity) * 100
                })
            }
            workOrder.progress = this.getTotalPercentage(workOrder)
            workOrder.woQty = this.getTotalWOQty(workOrder)
            workOrder.progressData = this.getTotalProgressData(workOrder)
            workOrder.releasedPercentage = this.getTotalReleased(workOrder)
            workOrder.totalQualityItems = this.getTotalQualityItems(workOrder)
            workOrder.itemSubheaders = this.setSubheaders(workOrder.items || [])
            return workOrder
        },
        async setAdditionalReportWorkOrders(workOrders) {
            const hydrated = await this.woMethods.mapInBatches(
                workOrders,
                workOrder => this.prepareWorkOrder(workOrder),
                20
            )
            return this.structureData(hydrated)
        },
        getTotalWOQty(wo) {
            if (wo.items) {
                return wo.items.reduce(
                    (accum, item) => accum + Number(item.quantity),
                    0
                )
            } else {
                return 0
            }
        },
        clearListeners() {
            this.listeners.forEach(unsubscribe => {
                if (typeof unsubscribe === 'function') {
                    unsubscribe()
                }
            })
            this.listeners = []
            this.listener = null
        },
        workOrdersCollection() {
            return firestore
                .collection('companies')
                .doc(this.companyId)
                .collection('workOrders')
        },
        async loadAllActiveWorkOrders() {
            return await this.woMethods.loadAllActiveWorkOrders(
                this.companyId,
                ['approved'],
                false,
                ['approved', 'finished']
            )
        },
        async loadApprovedFallback() {
            if (this.filterByProject) {
                const rows = await API.getWorkOrdersByProject({
                    projectId: this.projectId,
                    includeClosed: false,
                })
                return (rows || []).filter(
                    workOrder => workOrder && workOrder.status === 'approved'
                )
            }
            return this.loadAllActiveWorkOrders()
        },
        async loadWorkOrdersByCodes(codes) {
            const unique = [...new Set((codes || []).filter(Boolean))]
            const docs = []
            for (let i = 0; i < unique.length; i += FIRESTORE_IN_LIMIT) {
                const chunk = unique.slice(i, i + FIRESTORE_IN_LIMIT)
                const snapshot = await this.workOrdersCollection()
                    .where('code', 'in', chunk)
                    .get()
                snapshot.forEach(doc => {
                    docs.push(this.slimWorkOrder({ id: doc.id, ...doc.data() }))
                })
            }
            return docs
        },
        async loadFinishedForReport() {
            if (this.eventWorkOrders && this.eventWorkOrders.length > 0) {
                const rows = await this.loadWorkOrdersByCodes(
                    this.eventWorkOrders
                )
                return rows.filter(workOrder => workOrder.status === 'finished')
            }
            const rows = await API.getProgressSummary({
                status: 'finished',
                projectId: this.projectId || undefined,
            })
            return (rows || [])
                .map(workOrder => this.slimWorkOrder(workOrder))
                .filter(
                    workOrder => workOrder && workOrder.status === 'finished'
                )
        },
        slimWorkOrder(workOrder) {
            if (!workOrder) return null
            const source = workOrder.partNumbers || {}
            const partNumbers = {}
            Object.keys(source).forEach(partNumberId => {
                const partNumber = source[partNumberId] || {}
                partNumbers[partNumberId] = {
                    partNumber: partNumber.partNumber,
                    description: partNumber.description,
                    finishAndUCCode: partNumber.finishAndUCCode,
                    quantity: partNumber.quantity,
                    processes: Array.isArray(partNumber.processes)
                        ? partNumber.processes
                        : [],
                    packedQty: partNumber.packedQty,
                    production: Array.isArray(partNumber.production)
                        ? partNumber.production.map(entry => ({
                              qty: entry && entry.qty,
                              process: {
                                  id:
                                      entry &&
                                      entry.process &&
                                      entry.process.id,
                              },
                          }))
                        : [],
                    quality: Array.isArray(partNumber.quality)
                        ? partNumber.quality.map(entry => ({
                              releaseQty: entry && entry.releaseQty,
                          }))
                        : [],
                    deliveries: Array.isArray(partNumber.deliveries)
                        ? partNumber.deliveries.map(entry => ({
                              qty: entry && entry.qty,
                              receivedBy: entry && entry.receivedBy,
                              nextProcess: {
                                  id:
                                      entry &&
                                      entry.nextProcess &&
                                      entry.nextProcess.id,
                              },
                          }))
                        : [],
                }
            })
            let assemblyWorkOrder
            if (
                workOrder.assemblyWorkOrder === true ||
                workOrder.assemblyWorkOrder === 'true'
            ) {
                assemblyWorkOrder = true
            } else if (
                workOrder.assemblyWorkOrder === false ||
                workOrder.assemblyWorkOrder === 'false'
            ) {
                assemblyWorkOrder = false
            }
            return {
                id: workOrder.id,
                code: workOrder.code,
                status: workOrder.status,
                projectId: workOrder.projectId,
                projectManager: workOrder.projectManager,
                classification: workOrder.classification,
                assemblyWorkOrder,
                approvedDate: workOrder.approvedDate,
                productionStart: workOrder.productionStart,
                productionEnd: workOrder.productionEnd,
                productionFinish: workOrder.productionFinish,
                warranty: workOrder.warranty,
                notes: workOrder.notes,
                description: workOrder.description,
                partNumbers,
            }
        },
        isVisibleWorkOrder(workOrder) {
            if (!workOrder) return false
            if (this.eventWorkOrders && this.eventWorkOrders.length > 0) {
                return this.eventWorkOrders.includes(workOrder.code)
            }
            if (workOrder.status !== 'approved') return false
            if (this.projectId && workOrder.projectId !== this.projectId) {
                return false
            }
            return true
        },
        removeStructuredWorkOrder(id) {
            const index = this.structuredWorkOrders.findIndex(
                row => row.id === id
            )
            if (index >= 0) {
                this.structuredWorkOrders.splice(index, 1)
            }
        },
        async upsertStructuredWorkOrder(workOrder) {
            const hydrated = await this.prepareWorkOrder(
                this.slimWorkOrder(workOrder)
            )
            const structured = this.structureOne(hydrated)
            const index = this.structuredWorkOrders.findIndex(
                row => row.id === structured.id
            )
            if (index >= 0) {
                this.structuredWorkOrders.splice(index, 1)
            }
            const insertAt = this.structuredWorkOrders.findIndex(
                row => (row.progress || 0) < (structured.progress || 0)
            )
            if (insertAt === -1) {
                this.structuredWorkOrders.push(structured)
            } else {
                this.structuredWorkOrders.splice(insertAt, 0, structured)
            }
        },
        async applySnapshotChanges(changes) {
            for (const change of changes) {
                const workOrder = {
                    id: change.doc.id,
                    ...change.doc.data(),
                }
                if (
                    change.type === 'removed' ||
                    !this.isVisibleWorkOrder(workOrder)
                ) {
                    this.removeStructuredWorkOrder(workOrder.id)
                    continue
                }
                await this.upsertStructuredWorkOrder(workOrder)
            }
        },
        attachStatusListener() {
            const query = this.workOrdersCollection()
                .where('status', 'in', ['approved', 'finished'])
                .where('updatedOn', '>=', new Date())
            const unsubscribe = query.onSnapshot(
                snapshot => {
                    this.applySnapshotChanges(snapshot.docChanges()).catch(
                        error => {
                            console.error(
                                'Progress snapshot apply failed',
                                error
                            )
                        }
                    )
                },
                error => {
                    console.error('Progress listener failed', error)
                }
            )
            this.listeners.push(unsubscribe)
            this.listener = unsubscribe
        },
        attachCodeListeners(codes) {
            const unique = [...new Set((codes || []).filter(Boolean))]
            for (let i = 0; i < unique.length; i += FIRESTORE_IN_LIMIT) {
                const chunk = unique.slice(i, i + FIRESTORE_IN_LIMIT)
                let skipInitial = true
                const unsubscribe = this.workOrdersCollection()
                    .where('code', 'in', chunk)
                    .onSnapshot(
                        snapshot => {
                            if (skipInitial) {
                                skipInitial = false
                                return
                            }
                            this.applySnapshotChanges(
                                snapshot.docChanges()
                            ).catch(error => {
                                console.error(
                                    'Progress code snapshot apply failed',
                                    error
                                )
                            })
                        },
                        error => {
                            console.error(
                                'Progress code listener failed',
                                error
                            )
                        }
                    )
                this.listeners.push(unsubscribe)
            }
        },
        async getWOFromSDK() {
            const loadStarted = performance.now()
            const loadToken = (this.loadToken || 0) + 1
            this.loadToken = loadToken
            this.clearListeners()
            try {
                this.loading = true
                const filterByCodes =
                    this.eventWorkOrders && this.eventWorkOrders.length > 0
                let source = 'progress-summary'
                let initialData = []
                if (filterByCodes) {
                    source = 'event-codes'
                    initialData = await this.loadWorkOrdersByCodes(
                        this.eventWorkOrders
                    )
                    const foundCodes = new Set(
                        initialData.map(workOrder => workOrder.code)
                    )
                    const missingCodes = [
                        ...new Set(this.eventWorkOrders),
                    ].filter(code => !foundCodes.has(code))
                    if (missingCodes.length && this.initialLoad) {
                        this.initialLoad = false
                        const extra = await Promise.all(
                            missingCodes.map(async code => {
                                try {
                                    return await API.findWOByCode(code)
                                } catch (error) {
                                    console.error(
                                        'Progress find by code failed',
                                        code,
                                        error
                                    )
                                    return null
                                }
                            })
                        )
                        extra.filter(Boolean).forEach(workOrder => {
                            initialData.push(this.slimWorkOrder(workOrder))
                        })
                    }
                } else {
                    try {
                        initialData = await API.getProgressSummary({
                            status: 'approved',
                            projectId: this.filterByProject
                                ? this.projectId
                                : undefined,
                        })
                    } catch (error) {
                        console.error(
                            'Progress summary failed, using fallback',
                            error
                        )
                        source = this.filterByProject
                            ? 'find-by-project'
                            : 'backup-json'
                        initialData = await this.loadApprovedFallback()
                    }
                    initialData = (initialData || [])
                        .map(workOrder => this.slimWorkOrder(workOrder))
                        .filter(
                            workOrder =>
                                workOrder && workOrder.status === 'approved'
                        )
                    if (this.filterByProject && this.projectId) {
                        initialData = initialData.filter(
                            workOrder => workOrder.projectId === this.projectId
                        )
                    }
                }
                await this.setWorkOrders(initialData)
                this.lastLoadMetrics = {
                    source,
                    count: this.structuredWorkOrders.length,
                    ms: Math.round(performance.now() - loadStarted),
                }
                if (this._isDestroyed || loadToken !== this.loadToken) {
                    return
                }
                if (filterByCodes) {
                    this.attachCodeListeners(this.eventWorkOrders)
                } else {
                    this.attachStatusListener()
                }
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async setWorkOrders(workOrders) {
            const hydrated = await this.woMethods.mapInBatches(
                workOrders || [],
                workOrder => this.prepareWorkOrder(workOrder),
                20
            )
            this.structuredWorkOrders = this.structureData(hydrated)
        },
        async ensureProject(projectId) {
            if (!projectId) return null
            if (this.projects[projectId]) return this.projects[projectId]
            if (!this.projectRequests) this.projectRequests = {}
            if (!this.projectRequests[projectId]) {
                this.projectRequests[projectId] = API.getProject(projectId)
                    .then(project => {
                        this.$set(this.projects, projectId, project)
                        return project
                    })
                    .catch(error => {
                        console.error('Progress project lookup failed', error)
                        const fallback = {
                            id: projectId,
                            name: '',
                            reference: '',
                        }
                        this.$set(this.projects, projectId, fallback)
                        return fallback
                    })
            }
            return this.projectRequests[projectId]
        },
        async prepareWorkOrder(workOrder) {
            const wo = { ...workOrder }
            const projectManagerId =
                wo.projectManager && wo.projectManager.id
                    ? wo.projectManager.id
                    : wo.projectManager
            const manager = this.users[projectManagerId]
            if (manager) {
                wo.projectManager = manager
            } else if (!(wo.projectManager && wo.projectManager.name)) {
                const fallbackManager = {
                    id: projectManagerId,
                    name: '',
                    permissions: [],
                    process: [],
                }
                wo.projectManager = fallbackManager
                if (projectManagerId) {
                    this.users[projectManagerId] = fallbackManager
                }
            }

            if (wo.projectId) {
                wo.project =
                    this.projects[wo.projectId] ||
                    (await this.ensureProject(wo.projectId))
            }

            this.formatItems(wo)

            this.$set(
                wo,
                'deliveryDate',
                this.deliveryDatesPerWO[wo.code] ?? null
            )

            return wo
        },
        isNonEffectiveProcess(processId, index, processesLength) {
            if (
                processId === 'quality' ||
                processId === 'packing' ||
                processId === this.qualityProcess.id ||
                processId === this.packingProcess.id ||
                processId === this.readyForPackingProcess.id
            ) {
                return true
            }
            if (index === processesLength - 1) {
                const process = this.settingProcesses?.processes?.find(
                    p => p.id === processId
                )
                if (process?.assemblyProcess) {
                    return true
                }
            }
            return false
        },
        getItemLastEffectiveProcessId(item) {
            if (!item?.processes?.length) return null

            for (let i = item.processes.length - 1; i >= 0; i--) {
                const processId = item.processes[i]
                if (
                    this.isNonEffectiveProcess(
                        processId,
                        i,
                        item.processes.length
                    )
                ) {
                    continue
                }
                return processId
            }
            return null
        },
        getItemEffectiveProducedQty(item) {
            if (!item?.processes?.length || !item.progressData) return 0

            const lastProcessId = this.getItemLastEffectiveProcessId(item)
            if (!lastProcessId) return 0

            const progressKey =
                lastProcessId == this.readyForPackingProcess.id
                    ? this.packingProcess.id
                    : lastProcessId

            return Number(
                item.progressData[progressKey] ??
                    item.progressData[lastProcessId] ??
                    0
            )
        },
        getEffectiveProducedQty(workOrder) {
            if (!workOrder?.items) return 0
            return workOrder.items.reduce(
                (total, item) => total + this.getItemEffectiveProducedQty(item),
                0
            )
        },
        getTotalPercentage(wo) {
            if (wo.items) {
                let produced = 0
                let total = 0
                wo.items.forEach(item => {
                    let progressDataCount = 0
                    Object.keys(item.progressData || {}).forEach(processId => {
                        if (
                            processId != this.packingProcess.id &&
                            processId != this.qualityProcess.id
                        ) {
                            produced += Number(
                                item.progressData[processId] || 0
                            )
                            progressDataCount++
                        }
                    })
                    total += Number(item.quantity * progressDataCount)
                })
                if (total > 0) {
                    return Math.min((produced / total) * 100, 100)
                } else {
                    return 0
                }
            } else {
                return 0
            }
        },
        getTotalProgressData(workOrder) {
            let progressData = {}
            if (workOrder.items) {
                workOrder.items.forEach(item => {
                    if (item.processes.includes(this.qualityProcess.id)) {
                        const qualityId = this.qualityProcess.id
                        // initialize if not exist
                        progressData[qualityId] = progressData[qualityId] || {
                            produced: 0,
                            total: 0,
                        }
                        progressData[qualityId].produced += +item.released || 0
                        progressData[qualityId].total +=
                            +item.releasableQty || 0
                    }
                    if (item.progressData) {
                        Object.keys(item.progressData).forEach(processId => {
                            if (processId != this.qualityProcess.id) {
                                // initialize if not exist
                                progressData[processId] = progressData[
                                    processId
                                ] || { produced: 0, total: 0 }
                                progressData[processId].produced += Number(
                                    item.progressData[processId]
                                )
                                progressData[processId].total += Number(
                                    item.quantity
                                )
                            }
                        })
                    }
                })
            }
            return progressData
        },
        getTotalReleased(wo) {
            if (wo.items) {
                let released = 0
                let total = 0
                wo.items.forEach(item => {
                    if (item.showReleased) {
                        wo.showReleased = true
                        total += Number(item.releasableQty || 0)
                        released += Number(item.released || 0)
                    }
                })
                if (total > 0) {
                    return (released / total) * 100
                } else {
                    return 0
                }
            } else {
                return 0
            }
        },
        getTotalQualityItems(wo) {
            let total = 0
            if (wo.items) {
                wo.items.forEach(item => {
                    if (item.showReleased) {
                        total += Number(item.quantity || 0)
                    }
                })
            }
            return total
        },
        formatItems(workOrder) {
            if (
                !(
                    (workOrder.status == 'closed' ||
                        workOrder.status == 'cancelled') &&
                    !workOrder.partNumbers
                )
            ) {
                delete workOrder.items
                workOrder.items = []
            }
            if (workOrder.partNumbers) {
                Object.keys(workOrder.partNumbers).forEach(partNumberId => {
                    const source = workOrder.partNumbers[partNumberId] || {}
                    const item = { partNumberId }
                    PROGRESS_PART_NUMBER_KEYS.forEach(key => {
                        if (source[key] !== undefined) {
                            item[key] = source[key]
                        }
                    })
                    if (!Array.isArray(item.processes)) {
                        item.processes = []
                    }
                    workOrder.items.push(item)
                })
                delete workOrder.partNumbers
            }
            return workOrder
        },
        getProgressColor(key, percentage) {
            if (key == 'count') return 'primary'
            if (percentage < 25) return 'error'
            if (percentage < 50) return 'orange'
            if (percentage < 75) return 'warning'
            if (percentage < 100) return 'info'
            return 'success'
        },
        setSubheaders(items) {
            let headers = []
            let addPacking = false
            let addQuality = false
            headers.push(...this.subheaders)
            items.forEach(i => {
                if (i.progressData) {
                    Object.keys(i.progressData).forEach(processId => {
                        if (processId == this.packingProcess.id) {
                            addPacking = true
                        } else if (processId == this.qualityProcess.id) {
                            addQuality = true
                        } else if (!headers.find(h => h.id == processId)) {
                            const processHeader = this.processHeaders.find(
                                header => header.id == processId
                            )
                            if (processHeader) {
                                headers.push(processHeader)
                            }
                        }
                    })
                }
            })
            if (addPacking) {
                headers.push({
                    text: this.packingProcess.name.toUpperCase(),
                    value: 'process',
                    id: this.packingProcess.id,
                    sortable: false,
                    align: 'center',
                    divider: true,
                    width: 120,
                })
            }
            if (addQuality) {
                headers.push({
                    text: this.qualityProcess.name.toUpperCase(),
                    value: 'process',
                    id: this.qualityProcess.id,
                    sortable: false,
                    align: 'center',
                    divider: true,
                    width: 120,
                })
            }
            return headers
        },
        filterByProcess(order) {
            if (order.progressData) {
                if (
                    order.progressData[this.selectedProcess.id] &&
                    (order.progressData[this.selectedProcess.id].produced ||
                        order.progressData[this.selectedProcess.id].total)
                ) {
                    return true
                } else return false
            } else return false
        },
        async ensureXlsx() {
            if (!this.xlsxLib) {
                this.xlsxLib = await import('xlsx/xlsx.mjs')
            }
            return this.xlsxLib
        },
        openExportReportDialog() {
            this.exportReportDialog = true
        },
        async downloadReport(type) {
            if (type === 'basic') {
                await this.exportToExcelBasic()
                return
            }
            if (type === 'extended') {
                await this.exportToExcelExtended()
            }
        },
        async exportToExcelBasic() {
            try {
                this.loading = true
                const XLSX = await this.ensureXlsx()
                const WOs = this.structuredWorkOrders

                const rows = []

                for (const wo of WOs) {
                    const workOrderCode = wo.code ?? ''
                    const project = wo.project
                        ? `${wo.project.reference ?? ''} - ${wo.project.name ??
                              ''}`
                        : ''
                    const projectManager = wo.projectManager?.name ?? ''

                    for (const item of wo.items ?? []) {
                        //produced qty
                        let lastProcessIndex = item.processes
                            .map((process, index) => ({ process, index }))
                            .filter(
                                ({ process }) =>
                                    process !== 'packing' &&
                                    process !== 'quality'
                            )
                            .pop()?.index
                        item.progressData = {}
                        if (item.processes) {
                            item.processes.forEach(processId => {
                                const process =
                                    processId == this.readyForPackingProcess.id
                                        ? this.packingProcess.id
                                        : processId
                                if (processId != this.qualityProcess.id) {
                                    item.progressData[process] = 0
                                }
                            })
                        }
                        if (item.production) {
                            item.production.forEach(entry => {
                                if (
                                    entry.process.id ==
                                    this.readyForPackingProcess.id
                                ) {
                                    entry.process.id == this.packingProcess.id
                                }

                                const entryProcess =
                                    entry.process.id ==
                                    this.readyForPackingProcess.id
                                        ? this.packingProcess.id
                                        : entry.process.id

                                if (entryProcess != this.packingProcess.id) {
                                    item.progressData[entryProcess] =
                                        item.progressData[entryProcess] || 0
                                    item.progressData[entryProcess] += Number(
                                        entry.qty
                                    )
                                }
                            })
                        }
                        if (item.packedQty) {
                            item.progressData[this.packingProcess.id] = Number(
                                item.packedQty
                            )
                        }
                        item.producedQty =
                            item.progressData[
                                item.processes[lastProcessIndex]
                            ] || 0

                        //last process
                        const qualityIndex = item.processes.findIndex(
                            p => p == this.qualityProcess.id
                        )
                        if (
                            item.quality &&
                            qualityIndex > -1 &&
                            item.processes.length - 2 == qualityIndex &&
                            (item.processes[qualityIndex + 1] ==
                                this.packingProcess.id ||
                                item.processes[qualityIndex + 1] ==
                                    this.readyForPackingProcess.id)
                        ) {
                            if (qualityIndex - 1 > -1) {
                                const process = this.settingProcesses.processes.find(
                                    pr =>
                                        pr.id ==
                                        item.processes[qualityIndex - 1]
                                )
                                if (process) {
                                    item.lastProcess = process.name
                                }
                            }
                        } else if (
                            wo.assemblyWorkOrder == undefined &&
                            item.quality
                        ) {
                            const process = this.settingProcesses.processes.find(
                                pr =>
                                    pr.id ==
                                    item.processes[item.processes.length - 1]
                            )
                            if (process) {
                                item.lastProcess = process.name
                            }
                        }

                        // Fallback: Use last process if not set
                        if (
                            item.lastProcess === undefined &&
                            item.processes &&
                            item.processes.length > 0
                        ) {
                            const lastProcessId =
                                item.processes[item.processes.length - 1]
                            const lastProcess = this.settingProcesses.processes.find(
                                pr => pr.id === lastProcessId
                            )
                            if (lastProcess) {
                                item.lastProcess = lastProcess.name
                            }
                        }
                        //qty receivedInPacking
                        if (item.deliveries) {
                            const deliveries = item.deliveries.filter(
                                register =>
                                    (register.nextProcess.id ==
                                        this.packingProcess.id ||
                                        register.nextProcess.id ==
                                            this.readyForPackingProcess.id) &&
                                    register.receivedBy
                            )
                            item.qtyReceivedInPacking = deliveries.reduce(
                                (total, register) =>
                                    total + Number(register.qty),
                                0
                            )
                        }
                        let rowData = {
                            workOrder: workOrderCode,
                            Project: project,
                            projectManager: projectManager,
                            partNumber: item.partNumber || '',
                            description: item.description || '',
                            FinishAndUCCode:
                                (item.finishAndUCCode &&
                                    this.finishAndUcCodeNames[
                                        item.finishAndUCCode
                                    ]) ||
                                '',
                            Qty: item.quantity || 0,
                            Progress: `${(item.progress || 0).toFixed(2)} %`,
                            ProducedQty: item.producedQty || 0,
                            lastProcess: item.lastProcess || '',
                            QCReleased: item.processes.includes(
                                this.qualityProcess.id
                            )
                                ? item.quantity
                                    ? `${(
                                          (+item.released * 100) /
                                          +item.quantity
                                      ).toFixed(2)}%`
                                    : '100%'
                                : null,
                            qtyReceivedInPacking:
                                item.qtyReceivedInPacking || 0,
                        }

                        rowData[
                            this.qualityProcess.id
                        ] = item.processes.includes(this.qualityProcess.id)
                            ? item.released || 0
                            : null

                        this.processHeaders.forEach(process => {
                            rowData[
                                this.formatText(process.text)
                            ] = item.processes.includes(process.id)
                                ? item.progressData[process.id] || 0
                                : null
                        })

                        rowData[
                            this.packingProcess.id
                        ] = item.processes.includes(this.packingProcess.id)
                            ? item.progressData[this.packingProcess.id] || 0
                            : null

                        rows.push(rowData)
                    }
                }

                if (rows.length === 0) {
                    this.setErrorItems({
                        source: this.$options.name,
                        message: 'There is no data to export.',
                    })
                    return
                }

                const ws = XLSX.utils.json_to_sheet(rows)

                const formattedDate = moment().format('YYYY-MM-DD_HH:mm')

                const wb = XLSX.utils.book_new()
                XLSX.utils.book_append_sheet(wb, ws, 'WorkOrdersProgress')
                XLSX.writeFile(wb, `work_orders_progress_${formattedDate}.xlsx`)
                this.exportReportDialog = false
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },

        async exportToExcelExtended() {
            try {
                this.loading = true
                const XLSX = await this.ensureXlsx()
                let packingLists = this.packingListLiteNormalize(
                    await API.getWOPackingListsLite()
                )
                //load clients
                const {
                    data: { clients },
                } = await API.getLiteClients()
                const finishedWorkOrders = await this.loadFinishedForReport()
                const additionalWOs = await this.setAdditionalReportWorkOrders(
                    finishedWorkOrders
                )
                let WOs = [...this.structuredWorkOrders, ...additionalWOs]

                const rows = []

                const formatDateYMD = value => {
                    const m = this.packingListLiteParseDate(value)
                    return m ? m.format('YYYY-MM-DD') : ''
                }

                for (const wo of WOs) {
                    // Deduplicar procesos entre items (evita que se repitan en wo.processes)
                    const flattenedProcesses = wo.items.flatMap(item =>
                        Array.isArray(item.processes) ? item.processes : []
                    )
                    wo.processes = Array.from(new Set(flattenedProcesses))
                    const workOrderCode = wo.code ?? ''
                    const status = wo.status ?? ''
                    const project = wo.project
                        ? `${wo.project.reference ?? ''} - ${wo.project.name ??
                              ''}`
                        : ''
                    const projectManager = wo.projectManager?.name ?? ''

                    // Client from project
                    const client =
                        clients.find(c => c.id === wo.project?.client)?.name ||
                        ''

                    const {
                        scheduledForMoment,
                        relevantPl,
                    } = this.packingListLiteDeliveryContext(
                        wo.code,
                        packingLists
                    )

                    const deliveryWeek =
                        scheduledForMoment && scheduledForMoment.isValid()
                            ? scheduledForMoment.isoWeek()
                            : ''

                    // Approval / production dates
                    const approvalDate = formatDateYMD(wo.approvedDate)
                    const productionStartMoment = this.packingListLiteParseDate(
                        wo.productionStart
                    )
                    const productionEndMoment = this.packingListLiteParseDate(
                        wo.productionEnd || wo.productionFinish
                    )

                    let productionDays = ''
                    if (productionStartMoment) {
                        const end = productionEndMoment || moment()
                        productionDays = end
                            .startOf('day')
                            .diff(productionStartMoment.startOf('day'), 'days')
                    }

                    let daysLate = null
                    if (scheduledForMoment && relevantPl?.shippedOn) {
                        const shipped = this.packingListLiteParseDate(
                            relevantPl.shippedOn
                        )
                        if (shipped?.isValid()) {
                            daysLate = shipped
                                .startOf('day')
                                .diff(scheduledForMoment.startOf('day'), 'days')
                        }
                    } else if (scheduledForMoment) {
                        daysLate = moment()
                            .startOf('day')
                            .diff(scheduledForMoment.startOf('day'), 'days')
                    }

                    const description =
                        wo.description || wo.project?.description || ''
                    const notes = wo.notes || ''

                    const rowData = {
                        deliveryWeek,
                        approvalDate,
                        productionStart: productionStartMoment
                            ? productionStartMoment.format('YYYY-MM-DD')
                            : '',
                        scheduledFor: scheduledForMoment
                            ? scheduledForMoment.format('YYYY-MM-DD')
                            : '',
                        productionDays,
                        daysLate,
                        client,
                        classification: wo.classification || '',
                        Project: project,
                        projectManager: projectManager,
                        workOrder: workOrderCode,
                        status,
                        warranty: wo.warranty ? 'Yes' : 'No',
                        description,
                        notes,
                        Qty: wo.woQty || 0,
                        ProducedQty: this.getEffectiveProducedQty(wo),
                        Progress: `${Math.min(wo.progress || 0, 100).toFixed(
                            2
                        )} %`,
                        QCReleased: wo.showReleased
                            ? typeof wo.releasedPercentage === 'number'
                                ? `${wo.releasedPercentage.toFixed(2)}%`
                                : null
                            : null,
                        qualityQuantity: wo.processes.includes(
                            this.qualityProcess.id
                        )
                            ? wo.progressData[this.qualityProcess.id]?.total ||
                              0
                            : null,
                    }

                    // Aggregate per-process, quality and packing data from workOrder.progressData
                    if (wo.progressData) {
                        if (wo.processes.includes(this.qualityProcess.id)) {
                            rowData[this.qualityProcess.id] =
                                wo.progressData[this.qualityProcess.id]
                                    .produced || 0
                        } else {
                            rowData[this.qualityProcess.id] = null
                        }

                        this.processHeaders.forEach(process => {
                            if (wo.processes.includes(process.id)) {
                                rowData[this.formatText(process.text)] =
                                    wo.progressData[process.id]?.produced || 0
                            } else {
                                rowData[this.formatText(process.text)] = null
                            }
                        })

                        if (wo.processes.includes(this.packingProcess.id)) {
                            rowData[this.packingProcess.id] =
                                wo.progressData[this.packingProcess.id]
                                    .produced || 0
                            rowData['packableQuantity'] =
                                wo.progressData[this.packingProcess.id].total ||
                                0
                        } else {
                            rowData[this.packingProcess.id] = null
                        }
                    }

                    rows.push(rowData)
                }

                if (rows.length === 0) {
                    this.setErrorItems({
                        source: this.$options.name,
                        message: 'There is no data to export.',
                    })
                    return
                }

                const extendedReportBaseHeaders = [
                    'deliveryWeek',
                    'approvalDate',
                    'productionStart',
                    'scheduledFor',
                    'productionDays',
                    'daysLate',
                    'client',
                    'classification',
                    'Project',
                    'projectManager',
                    'workOrder',
                    'status',
                    'warranty',
                    'description',
                    'notes',
                    'Qty',
                    'ProducedQty',
                    'Progress',
                    'QCReleased',
                    'qualityQuantity',
                ]

                const dynamicHeaders = []
                if (this.qualityProcess?.id) {
                    dynamicHeaders.push(this.qualityProcess.id)
                }
                this.processHeaders.forEach(process => {
                    dynamicHeaders.push(this.formatText(process.text))
                })
                if (this.packingProcess?.id) {
                    dynamicHeaders.push(this.packingProcess.id)
                    dynamicHeaders.push('packableQuantity')
                }

                const knownHeaders = new Set([
                    ...extendedReportBaseHeaders,
                    ...dynamicHeaders,
                ])
                const extraHeaders = [
                    ...new Set(rows.flatMap(row => Object.keys(row))),
                ].filter(key => !knownHeaders.has(key))

                const headers = [
                    ...extendedReportBaseHeaders,
                    ...dynamicHeaders,
                    ...extraHeaders,
                ]

                const ws = XLSX.utils.json_to_sheet(rows, { header: headers })

                const formattedDate = moment().format('YYYY-MM-DD_HH:mm')

                const wb = XLSX.utils.book_new()
                XLSX.utils.book_append_sheet(
                    wb,
                    ws,
                    'WorkOrdersProgressExtended'
                )
                XLSX.writeFile(
                    wb,
                    `work_orders_progress_extended_${formattedDate}.xlsx`
                )
                this.exportReportDialog = false
            } catch (error) {
                console.log(error)
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },

        formatText(text) {
            const lowerCaseText = text.toLowerCase()
            const words = lowerCaseText.split(' ')
            const formattedWords = words.map(word => {
                if (word) {
                    return word.charAt(0).toUpperCase() + word.slice(1)
                }
                return ''
            })
            return formattedWords.join('')
        },
        openFinishDialog(workOrder) {
            try {
                this.selectedWorkOrder = workOrder
                this.title = 'Finish'
                if (!this.allItemsProduced(workOrder)) {
                    this.message =
                        'You are about to finish the work order without completing the production of all processes, are you sure to carry out this operation?'
                } else {
                    this.message =
                        'Are you sure you want to finish this work order ?'
                }
                this.openWarning = true
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        closeWarning() {
            this.openWarning = false
            this.selectedWorkOrder = null
        },
        async finishWO() {
            if (!this.selectedWorkOrder) return
            const finishedId = this.selectedWorkOrder.id
            try {
                this.loading = true
                await API.updateWorkOrder({
                    workOrderId: finishedId,
                    status: 'finished',
                    notificationId: 'pFDh25EcR74EMiuypq8M',
                })
                this.closeWarning()
                this.removeStructuredWorkOrder(finishedId)
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        disableFinishProcess(workOrder) {
            // validate work order production
            let expectedProducedQuantity = 0
            let actualProducedQuantity = 0
            if (!workOrder.items || workOrder.items.length === 0) {
                return true
            }
            for (let item of workOrder.items) {
                // exclude quality and packing processes
                const targetProcesses = (item.processes || []).filter(
                    process =>
                        process != this.qualityProcess.id &&
                        process != this.packingProcess.id
                )
                // get expected produced quantity
                expectedProducedQuantity +=
                    (item.quantity || 0) * targetProcesses.length

                // get actual produced quantity from progressData
                if (item.progressData) {
                    Object.keys(item.progressData).forEach(processId => {
                        if (
                            processId != this.qualityProcess.id &&
                            processId != this.packingProcess.id
                        ) {
                            actualProducedQuantity += Number(
                                item.progressData[processId] || 0
                            )
                        }
                    })
                }
            }

            return expectedProducedQuantity > actualProducedQuantity
        },
        allItemsProduced(workOrder) {
            if (!workOrder.items || workOrder.items.length === 0) {
                return false
            }
            let itemsProduced = true
            for (let item of workOrder.items) {
                if (item.processes) {
                    for (let process of item.processes) {
                        if (
                            process == this.qualityProcess.id ||
                            process == this.packingProcess.id
                        ) {
                            continue
                        }
                        let totalProduced = 0
                        if (item.production) {
                            let production = item.production.filter(
                                register => register.process.id === process
                            )
                            totalProduced = production.reduce(
                                (total, register) =>
                                    total + Number(register.qty),
                                0
                            )
                        }
                        if (
                            process ==
                                item.processes[item.processes.length - 1] &&
                            item.deliveries
                        ) {
                            let received = item.deliveries.filter(
                                register =>
                                    register.nextProcess.id == process &&
                                    register.receivedBy
                            )
                            const totalReceived = received.reduce(
                                (total, register) =>
                                    total + Number(register.qty),
                                0
                            )
                            itemsProduced =
                                totalReceived == Number(item.quantity)
                        } else if (totalProduced < item.quantity) {
                            itemsProduced = false
                            break
                        }
                    }
                }
                if (!itemsProduced) {
                    break
                }
            }
            return itemsProduced
        },
    },
}
</script>

<style>
.v-input .v-label {
    font-size: 12px;
}
.v-data-table-header th {
    background-color: #eeeeee !important;
}
.v-data-table__wrapper {
    max-height: 72vh;
    overflow-y: auto;
}
.v-data-table__row {
    height: 64px;
}
.no-interaction {
    pointer-events: none;
}
.subtable {
    z-index: 1;
    background-color: #eeeeee;
}
.progress-toolbar-filters {
    gap: 12px;
}
.progress-toolbar-filters .v-input {
    margin-top: 0 !important;
    padding-top: 0 !important;
}
</style>
